import { createLink, LinkComponent } from '@tanstack/react-router'
import { cva, VariantProps } from 'class-variance-authority'
import { AnchorHTMLAttributes, forwardRef } from 'react'

import { cn } from '@/ui/utils/style'

interface InternalLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  variant?: VariantProps<typeof linkVariants>['variant']
  'data-testid'?: string
}

const InternalLinkBaseComponent = createLink(
  forwardRef<HTMLAnchorElement, InternalLinkProps>(({ variant, className, ...props }, ref) => {
    return <a ref={ref} className={cn(linkVariants({ variant }), className)} {...props} />
  }),
)

// eslint-disable-next-line func-style
export const InternalLink: LinkComponent<typeof InternalLinkBaseComponent> = (props) => {
  return <InternalLinkBaseComponent {...props} />
}

export interface ExternalLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string
  variant?: VariantProps<typeof linkVariants>['variant']
  'data-testid'?: string
}

export const ExternalLink = forwardRef<HTMLAnchorElement, ExternalLinkProps>(
  ({ to, variant, className, ...props }, ref) => {
    return (
      <a
        {...props}
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(linkVariants({ variant }), className)}
        ref={ref}
      />
    )
  },
)

const linkVariants = cva('', {
  variants: {
    variant: {
      primary: 'bg-gradient-spark-secondary bg-clip-text text-transparent hover:brightness-90 active:brightness-80',
      secondary: 'text-brand-primary hover:brightness-150 active:brightness-200',
      underline: 'underline underline-offset-2',
      decorator: '',
      unstyled: '',
    },
  },
  defaultVariants: {
    variant: 'primary',
  },
  compoundVariants: [
    {
      variant: ['primary', 'secondary', 'decorator'],
      className: cn(
        'cursor-pointer rounded-[1px] focus-visible:outline-hidden focus-visible:ring-3',
        'focus-visible:ring-primary-200 focus-visible:ring-offset-0',
      ),
    },
  ],
})
